import React, { useState, useCallback, useMemo } from 'react';
import { Timeline, TimelineEvent, TimelineFilters } from '../history/shared/Timeline';
import { FilterValues } from '../history/shared/FilterPanel';
import { UserRole, RoleOption } from '../history/shared/RoleSwitcher';
import { SearchQuery } from '../history/shared/SearchBar';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { SkeletonSwitch, TemplateTimeLineItemSkeleton } from 'skeleton';
import './EnhancedTemplateHistory.css';

export interface TemplateHistoryData {
  templateId: string;
  templateName: string;
  version: number;
  changeType: 'created' | 'modified' | 'deleted' | 'restored' | 'published' | 'archived';
  changedElements: string[];
  collaborators: string[];
  branchName?: string;
  mergeRequestId?: string;
  usageStats?: {
    timesUsed: number;
    associatedCampaigns: string[];
    lastUsed?: Date;
  };
  diffSummary?: {
    additions: number;
    deletions: number;
    modifications: number;
  };
}

export interface TemplateHistoryEntry {
  id: string;
  type: string;
  timestamp: Date;
  title: string;
  description: string;
  userId: string;
  userName: string;
  templateData: TemplateHistoryData;
  metadata: Record<string, any>;
}

export interface EnhancedTemplateHistoryProps {
  onEventClick?: (event: TimelineEvent) => void;
  userRole?: UserRole;
  userPermissions?: string[];
  enableRealTimeUpdates?: boolean;
  className?: string;
  /** Controlled role — lifted from parent (HistoryView) */
  currentRole?: UserRole;
  /** Notify parent when role changes */
  onRoleChange?: (role: UserRole) => void;
}

export const templateHistoryRoles: RoleOption[] = [
  {
    role: 'customer',
    label: 'Customer',
    description: 'View your own template history',
    icon: '👤',
    permissions: ['view_own_templates'],
    available: true
  },
  {
    role: 'agent',
    label: 'Agent',
    description: 'View assigned template history with collaboration details',
    icon: '👨‍💼',
    permissions: ['view_assigned_templates', 'view_collaboration'],
    available: true
  },
  {
    role: 'manager',
    label: 'Manager',
    description: 'View team template history with analytics',
    icon: '👩‍💼',
    permissions: ['view_team_templates', 'view_analytics', 'view_collaboration'],
    available: true
  },
  {
    role: 'admin',
    label: 'Admin',
    description: 'View all template history with system data',
    icon: '⚙️',
    permissions: ['view_all_templates', 'view_system_data', 'view_analytics'],
    available: true
  }
];

export const EnhancedTemplateHistory: React.FC<EnhancedTemplateHistoryProps> = ({
  onEventClick,
  userRole = 'admin',
  userPermissions: _userPermissions = [],
  className = '',
  currentRole: controlledRole,
  onRoleChange: notifyRoleChange,
}) => {
  // Internal role state — used only when not controlled externally
  const [internalRole, setInternalRole] = useState<UserRole>(userRole);
  const currentRole = controlledRole ?? internalRole;

  const [_filters, setFilters] = useState<FilterValues>({});
  const [searchQuery, _setSearchQuery] = useState<SearchQuery | null>(null);
  const [isLoading, _setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convert template history entries to timeline events
  const timelineEvents = useMemo((): TimelineEvent[] => {
    let filteredHistory: TemplateHistoryEntry[] = [];

    if (currentRole === 'customer') {
      filteredHistory = filteredHistory.filter(entry => entry.userId === 'N/A');
    }

    if (searchQuery?.text) {
      const searchText = searchQuery.text.toLowerCase();
      filteredHistory = filteredHistory.filter(entry =>
        entry.title.toLowerCase().includes(searchText) ||
        entry.description.toLowerCase().includes(searchText) ||
        entry.templateData.templateName.toLowerCase().includes(searchText)
      );
    }

    return filteredHistory.map(entry => ({
      id: entry.id,
      type: entry.type,
      timestamp: entry.timestamp,
      title: entry.title,
      description: entry.description,
      icon: getEventIcon(entry.type),
      color: getEventColor(entry.type),
      metadata: {
        ...entry.metadata,
        templateData: entry.templateData,
        userName: entry.userName,
        userId: entry.userId
      }
    }));
  }, [currentRole, searchQuery]);

  // Timeline filters — only pass search text, filtering done in timelineEvents
  const timelineFilters: TimelineFilters = useMemo(() => {
    const tFilters: TimelineFilters = {};
    if (searchQuery?.text) tFilters.searchText = searchQuery.text;
    return tFilters;
  }, [searchQuery]);

  const _handleRoleChange = useCallback((newRole: UserRole) => {
    setInternalRole(newRole);
    notifyRoleChange?.(newRole);
    setFilters({});
  }, [notifyRoleChange]);

  const handleEventClick = useCallback((event: TimelineEvent) => {
    console.log('Template history event clicked:', event);
    if (event.metadata?.templateData) {
      const templateData = event.metadata.templateData as TemplateHistoryData;
      console.log('Template data:', templateData);
      if (templateData.diffSummary) {
        console.log('Changes:', templateData.diffSummary);
      }
    }
    onEventClick?.(event);
  }, [onEventClick]);

  // Custom renderers — all preserved exactly
  const customRenderers = useMemo(() => ({
    template_modified: (event: TimelineEvent) => {
      const templateData = event.metadata?.templateData as TemplateHistoryData;
      const diffSummary = templateData?.diffSummary;
      return (
        <div className="template-modification-event">
          {diffSummary && (
            <div className="diff-summary">
              <span className="diff-stat additions">+{diffSummary.additions}</span>
              <span className="diff-stat deletions">-{diffSummary.deletions}</span>
              <span className="diff-stat modifications">~{diffSummary.modifications}</span>
            </div>
          )}
          {templateData?.changedElements && templateData.changedElements.length > 0 && (
            <div className="changed-elements">
              <strong>Changed elements:</strong> {templateData.changedElements.join(', ')}
            </div>
          )}
          {templateData?.collaborators && templateData.collaborators.length > 1 && (
            <div className="collaborators">
              <strong>Collaborators:</strong> {templateData.collaborators.length} users
            </div>
          )}
        </div>
      );
    },

    template_created: (event: TimelineEvent) => {
      const templateData = event.metadata?.templateData as TemplateHistoryData;
      return (
        <div className="template-creation-event">
          {templateData?.changedElements && (
            <div className="initial-elements">
              <strong>Initial elements:</strong> {templateData.changedElements.join(', ')}
            </div>
          )}
          {templateData?.usageStats && (
            <div className="usage-stats">
              <span>Used {templateData.usageStats.timesUsed} times</span>
              {templateData.usageStats.associatedCampaigns.length > 0 && (
                <span> • {templateData.usageStats.associatedCampaigns.length} campaigns</span>
              )}
            </div>
          )}
        </div>
      );
    },

    template_published: (event: TimelineEvent) => {
      const templateData = event.metadata?.templateData as TemplateHistoryData;
      return (
        <div className="template-publication-event">
          <div className="publication-info">
            <span className="version-badge">v{templateData?.version}</span>
            <span className="status-badge published">Published</span>
          </div>
          {templateData?.usageStats && (
            <div className="usage-stats">
              <span>Used {templateData.usageStats.timesUsed} times since publication</span>
            </div>
          )}
        </div>
      );
    }
  }), []);

  function getEventIcon(type: string): string {
    switch (type) {
      case 'template_created':   return '📄';
      case 'template_modified':  return '✏️';
      case 'template_published': return '🚀';
      case 'template_archived':  return '📦';
      case 'template_deleted':   return '🗑️';
      case 'template_restored':  return '♻️';
      default: return '📄';
    }
  }

  function getEventColor(type: string): string {
    switch (type) {
      case 'template_created':   return '#10b981';
      case 'template_modified':  return '#3b82f6';
      case 'template_published': return '#8b5cf6';
      case 'template_archived':  return '#6b7280';
      case 'template_deleted':   return '#ef4444';
      case 'template_restored':  return '#f59e0b';
      default: return '#6b7280';
    }
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div className={`flex flex-col ${className}`}>
        <div className="m-5 flex flex-col items-center justify-center py-12 px-6 text-center
                        rounded-xl border border-rose-200 dark:border-rose-800/40
                        bg-rose-50/50 dark:bg-rose-900/10">
          <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mb-3">
            <AlertCircle className="w-5 h-5 text-rose-500 dark:text-rose-400" strokeWidth={2} />
          </div>
          <h3 className="text-[14px] font-semibold text-content dark:text-white mb-1">
            Error Loading Template History
          </h3>
          <p className="text-[13px] text-content-muted dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => setError(null)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-surface dark:bg-dark-800
                       border border-line dark:border-dark-600 rounded-lg shadow-sm
                       text-[13px] font-medium text-gray-700 dark:text-gray-200
                       hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
      </div>
    );
  }

  /* ── Main return — no toolbar, just content ── */
  return (
    <div className={`flex flex-col pt-4 ${className}`}>
      <SkeletonSwitch
        isLoading={isLoading}
        skeleton={
          <div className="p-4 bg-gray-50/60 dark:bg-white/[0.02]">
            {Array.from({ length: 5 }).map((_, i) => (
              <TemplateTimeLineItemSkeleton key={i} />
            ))}
          </div>
        }
      >
        <Timeline
          events={timelineEvents}
          context="template"
          filters={timelineFilters}
          onEventClick={handleEventClick}
          onFilterChange={() => {}}
          customRenderers={customRenderers}
          showAnalytics={currentRole === 'manager' || currentRole === 'admin'}
          className="w-full"
        />
      </SkeletonSwitch>
    </div>
  );
};

export default EnhancedTemplateHistory;
